<script>
import { join, leave, reactivate } from '../api/groups';
import VClamp from 'vue-clamp';
import moment from 'moment';

export default {
  name: 'CpGroup',
  components: {
    CpImage: require('../image').default,
    CpGrid: require('../grid').default,
    CpGridItem: require('../grid-item').default,
    CpButton: require('../button').default,
    CpBox: require('../box').default,
    CpBoxContent: require('../box-content').default,
    VClamp,
    CpButtonLink: require('../button-link').default,
    CpLeaveGroupDialog: require('../dialogs/leave-group').default,
    CpIcon: require('../icon').default,
    CpStateIcon: require('../social-feed/icons/state-icon').default,
    CpSocialShareDialog: require('../dialogs/social-share').default,
    CpStatusGroupDialog: require('../dialogs/status-group').default,
    CpDialogAuthForm: require('../auth-form/dialog-auth-form').default,
    CpShowImageInCard: require('../components/ShowImageInCard').default,
    CpTruncateText: require('../components/TruncateText').default,
  },
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginRight: require('../directives/margins').marginRight,
    marginBottom: require('../directives/margins').marginBottom,
    marginLeft: require('../directives/margins').marginLeft,
  },
  props: {
    group: { type: Object },
    currentUser: { type: Object },
    type: String,
  },
  data() {
    return {
      loading: false,
      reactivateLoading: false,
      showGroupDialog: false,
      showShareModal: false,
      showStatusModal: false,
      isDeactivated: this.group.moderation_state === 'deactivated' || false,
      deactivatedDate: this.group.deactivated_at && moment(this.group.deactivated_at).format('LL'),
      showAuthModal: false,
    };
  },
  computed: {
    authUser() {
      return this.$root.authUser;
    },
    descriptionText() {
      var filter = require('leo-profanity');

      //remove html tags so that vue clamp works properly
      return filter.clean(this.group.description.replace(/(<([^>]+)>)/gi, '').replace(/&nbsp;/gi, ' '), '*', 2);
    },
    groupTitle() {
      return this.group.title;
    },
    sanitizedGroupTitle() {
      const tempElem = document.createElement('div');
      tempElem.innerHTML = this.groupTitle;
      return tempElem.innerHTML;
    },
    groupPrivacy() {
      return this.group.public === 0 ? 'Private' : 'Public';
    },
    groupDescription() {
      return this.group.description;
    },
    sanitizedGroupDescription() {
      const tempElem = document.createElement('div');
      tempElem.innerHTML = this.groupDescription;
      return tempElem.innerHTML;
    },
    randomGradient() {
      const colorArray = ['blue', 'green', 'purple', 'pink', 'orange', 'dark-blue', 'black'];
      const randomIndex = Math.floor(Math.random() * Math.floor(8));

      return colorArray[randomIndex];
    },
    buttonLink() {
      if (this.group.show_onboarding === 0) {
        return this.groupDetails(this.group.id);
      } else {
        return this.groupStatus(this.group.id);
      }
    },
    titleForStatusTag() {
      if (this.group.moderation_state === 'approved') {
        return 'ACTIVE';
      } else if (this.group.moderation_state === 'rejected') {
        return 'REJECTED';
      } else if (this.group.moderation_state === 'deactivated') {
        return `DISABLED ON ${this.deactivatedDate.toUpperCase()}`;
      } else {
        return 'UNDER REVIEW';
      }
    },
  },
  methods: {
    groupDetails(id) {
      return '/groups/' + id;
    },
    groupStatus(id) {
      return '/groups/' + id + '/status';
    },
    groupUpdate(id) {
      return '/groups/' + id + '/update';
    },
    groupSettings(id) {
      return '/groups/' + id + '/settings';
    },
    joinGroup() {
      if (!this.authUser) {
        this.showAuthModal = true;
      } else {
        this.handleJoinGroup();
      }
    },
    handleJoinGroup() {
      this.loading = true;
      join(this.group.id)
        .then(response => {
          if (this.group.public == 0) {
            this.group.user_status = 'pending';
          } else {
            this.group.user_status = 'accepted';
            this.group.members_count += 1;
          }
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          console.warn(error);
        });
    },
    handleCloseButton() {
      this.showAuthModal = false;
    },
    cancelRequestJoinGroup() {
      if (!this.authUser) {
        window.location.href = '/user/login';
      } else {
        this.loading = true;
        leave(this.group.id)
          .then(() => {
            this.group.user_status = null;
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            console.error(error);
          });
      }
    },
    handleGroupDialog() {
      this.showGroupDialog = !this.showGroupDialog;
    },
    handleShareModal() {
      this.showShareModal = !this.showShareModal;
    },
    // Deactivate - Reactivate
    handleShowStatusModal() {
      this.showStatusModal = !this.showStatusModal;
    },
    reactivateGroup() {
      if (!this.authUser) {
        window.location.href = '/user/login';
      } else {
        this.reactivateLoading = true;
        reactivate(this.group.id)
          .then(response => {
            this.group.moderation_state = response.group.moderation_state;
            this.reactivateLoading = false;
          })
          .catch(error => {
            this.reactivateLoading = false;
            console.warn(error);
          });
      }
    },
  },
};
</script>

<template>
  <cp-box class="wrapper">
    <div v-margin-right="1" v-margin-left="1">
      <!-- Title -->
      <div v-margin-bottom="1">
        <v-clamp :max-lines="2" :class="{ 'black-color': true, H3: !$root.isMobile, subheadline1: $root.isMobile }" v-html="sanitizedGroupTitle" />
      </div>

      <CpShowImageInCard v-if="group.type != 'zipcode'" :image="group.image" :link="groupDetails(group.id)" />

      <div v-else class="social-feed-post-img-placeholder box-state-icon" :class="randomGradient">
        <div class="stateIconContainer">
          <cp-state-icon :american-state="group.state_abbreviation" groupFeedStyle />
          <span v-if="group.official" class="officialTagContainer caption 2 official-color">OFFICIAL</span>
        </div>
      </div>

      <!-- Privacy and members -->
      <div v-margin-top="1" v-margin-bottom="1">
        <span :class="{ buttons: !$root.isMobile, footnote: $root.isMobile, 'neutral5-color': true }"
          >{{ groupPrivacy + ' ' }}<span :class="{ body: !$root.isMobile, footnote: $root.isMobile, 'nuetral4-color': true }">group</span></span
        >
        <span
          v-if="group.moderation_state === 'approved'"
          :class="{ buttons: !$root.isMobile, footnote: $root.isMobile, 'neutral5-color': true }"
          v-margin-left="0.5"
          >{{ group.members_count }}<span :class="{ body: !$root.isMobile, footnote: $root.isMobile, 'nuetral4-color': true }">&nbsp;members</span></span
        >
        <span
          v-margin-left="0.5"
          v-if="group.user_status === 'owner'"
          :class="{
            footnote: true,
            'links-color approvedTag': group.moderation_state === 'approved',
            'news-title-color pendingTag': group.moderation_state === 'pending',
            'error-color rejectedTag': group.moderation_state === 'rejected',
            'neutral6-color deactivatedTag': group.moderation_state === 'deactivated',
          }"
        >
          {{ titleForStatusTag }}
        </span>
      </div>

      <!-- Description -->
      <CpTruncateText :content="groupDescription" />

      <!-- Tags -->
      <div v-if="group.tags" class="box-tags">
        <p v-for="tag in group.tags" class="tag footnote neutral4-color">{{ tag.name }}</p>
      </div>

      <!-- Handle buttons -->
      <cp-grid v-if="group.public && group.user_status === null">
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button :disabled="loading" :loading="loading" @click="joinGroup()" primary block><span class="caption1">Join</span></cp-button>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button-link :disabled="loading" :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="!group.public && (group.user_status === null || group.user_status === 'rejected')">
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button :disabled="loading" :loading="loading" @click="joinGroup()" primary block><span class="caption1">Request to join</span></cp-button>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button-link :disabled="loading" :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="!group.public && group.user_status === 'pending'">
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button @click="cancelRequestJoinGroup" :loading="loading" primary block><span class="caption1">Cancel Request</span></cp-button>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button-link :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'accepted' && group.moderation_state !== 'deactivated'">
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button-link primary :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button block @click="handleShareModal()"
            ><span class="shareBtnContainer"
              ><i class="fa fa-share share-icon" /><span v-if="!$root.isMobile" class="caption1">&nbsp;Share group</span></span
            ></cp-button
          >
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'owner' && group.moderation_state === 'approved'">
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button-link primary :href="buttonLink" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button-link :href="groupSettings(group.id)" v-if="$root.isMobile" block
            ><cp-icon class="cog-icon" scale="1.2" name="cog"></cp-icon
          ></cp-button-link>
          <cp-button-link :href="groupSettings(group.id)" v-else block><span class="caption1">Settings</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'owner' && group.moderation_state === 'deactivated'">
        <cp-grid-item span="4" v-margin-right="0.5">
          <cp-button primary block @click="handleShowStatusModal" :disabled="reactivateLoading" :loading="reactivateLoading"
            ><span class="caption1">Reactivate</span></cp-button
          >
        </cp-grid-item>
        <cp-grid-item span="4" v-margin-right="0.5">
          <cp-button-link :disabled="reactivateLoading" :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
        <cp-grid-item span="4" v-margin-left="0.5">
          <cp-button-link :href="groupSettings(group.id)" v-if="$root.isMobile" block
            ><cp-icon class="cog-icon" scale="1.2" name="cog"></cp-icon
          ></cp-button-link>
          <cp-button-link :href="groupSettings(group.id)" v-else block><span class="caption1">Settings</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'accepted' && group.moderation_state === 'deactivated'">
        <cp-grid-item span="12">
          <cp-button-link :href="groupDetails(group.id)" block><span class="caption1">Details</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'owner' && group.moderation_state === 'pending'">
        <cp-grid-item span="12">
          <cp-button-link :href="groupStatus(group.id)" primary block><span class="caption1">Check the details</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else-if="group.user_status === 'owner' && group.moderation_state === 'rejected'">
        <cp-grid-item span="12">
          <cp-button-link :href="groupStatus(group.id)" primary block><span class="caption1">Check the details</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>

      <cp-grid v-else>
        <cp-grid-item span="6" v-margin-right="0.5">
          <cp-button :disabled="loading" :loading="loading" @click="joinGroup()" primary block><span class="caption1">Join</span></cp-button>
        </cp-grid-item>
        <cp-grid-item span="6" v-margin-left="0.5">
          <cp-button-link :disabled="loading" :href="groupDetails(group.id)" block><span class="caption1">View</span></cp-button-link>
        </cp-grid-item>
      </cp-grid>
    </div>

    <!-- Modals -->
    <cp-leave-group-dialog v-if="showGroupDialog" :showDialog="showGroupDialog" :group="group" :handleDialog="handleGroupDialog"></cp-leave-group-dialog>
    <cp-social-share-dialog
      v-if="showShareModal"
      class="feed"
      :openShare="true"
      :handleShareModal="handleShareModal"
      :allow-share-feed="true"
      :type="'groups'"
      :post="group"
      share-category="Group"
      share-button-class="button--noborder"
      :url="group.permalink"
      title="Group found via CrowdBlue - Social Network For Politics"
      :description="`Wanted to share this group I found on CrowdBlue, the Social Network for Politics.`"
      hashtags="crowdBlue,politics,political crowdfunding"
      twitter-user="crowdblue"
    ></cp-social-share-dialog>
    <cp-status-group-dialog
      v-if="showStatusModal"
      :group="group"
      :handleDialog="handleShowStatusModal"
      :isDeactivated="isDeactivated"
      :handleStatusGroup="
        () => {
          this.isDeactivated = !this.isDeactivated;
        }
      "
    />
    <cp-dialog-auth-form v-if="showAuthModal" :handleCloseButton="handleCloseButton" showSignUp :callback-action="handleJoinGroup" />
  </cp-box>
</template>

<style scoped>
div.social-feed-post-img-placeholder.box-state-icon.pink {
  background: linear-gradient(90deg, #d53369 0%, #daae51 100%);
}
div.social-feed-post-img-placeholder.box-state-icon.orange {
  background: linear-gradient(to right, #f83600, #fe8c00);
}
div.social-feed-post-img-placeholder.box-state-icon.blue {
  background: linear-gradient(90deg, #1cb5e0 0%, #000851 100%);
}
div.social-feed-post-img-placeholder.box-state-icon.dark-blue {
  background: linear-gradient(90deg, #4b6cb7 0%, #182848 100%);
}
div.social-feed-post-img-placeholder.box-state-icon.green {
  background: #0eb45b;
  background: var(--green);
  background: linear-gradient(to right, #0eb45b, #57cd8e);
  background: linear-gradient(to right, var(--green), var(--green-lighter));
}
div.social-feed-post-img-placeholder.box-state-icon.purple {
  background: linear-gradient(to right, #5e18af, rgba(31, 38, 103, 0.9));
}
div.social-feed-post-img-placeholder.box-state-icon.black {
  background-color: #000000;
  background: linear-gradient(to right, #000000, #7f8c8d);
}
.box-state-icon {
  min-height: auto !important;
  width: 100%;
  position: relative;
}

.tag,
.approvedTag,
.pendingTag,
.rejectedTag,
.deactivatedTag {
  padding: 5px 10px;
  border-radius: 4px;
}
.tag {
  background-color: var(--white);
  margin-right: 5px;
  margin-top: 0px;
  margin-bottom: 0px;
  border: solid 1px var(--neutral3);
}
.deactivatedTag {
  background-color: var(--neutral3);
}
.box-tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: 15px;
  margin-bottom: 15px;
}
.image {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 4px;
}
.wrapper {
  align-items: flex-start;
  padding: 15px 8px;
  background: #fdfdfd;
  border-bottom: 2px solid var(--neutral3);
  border-radius: 4px;
}

.approvedTag {
  background-color: rgba(0, 161, 113, 0.2);
}
.pendingTag {
  background-color: rgba(237, 207, 47, 0.2);
}
.rejectedTag {
  background-color: rgba(241, 51, 0, 0.2);
}

.twoLinesDescription {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shareBtnContainer {
  display: flex;
  align-items: center;
}
.share-icon,
.cog-icon {
  font-size: 24px;
  color: var(--primary);
}

.share-icon {
  padding-right: 10px;
}
.defaultCursor {
  cursor: default;
}
.stateIconContainer {
  max-width: 75%;
  aspect-ratio: 16 / 9;
}
.officialTagContainer {
  background-color: var(--official-back);
  border-radius: 4px;
  padding: 4px 8px;
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 1;
}
@media only screen and (max-width: 768px) {
  .share-icon,
  .cog-icon {
    font-size: 16px;
    padding-right: 0px;
  }
}
</style>
